import React, { useState } from 'react';
import { Button, Menu } from '@mui/material';
import {
  AccountInfoItem,
  ApproveItem,
  ApproveItemLegacy,
  ArchiveItem,
  ArchiveItemLegacy,
  PhoneScreenPendingItem,
  PhoneScreenPendingItemLegacy,
  RejectItem,
  RejectItemLegacy,
  PhoneScreenScheduledItem,
  PhoneScreenScheduledItemLegacy,
  ResumeReviewItem,
} from './StatusItems';
import { useRecordContext, useShowContext, useUpdate, useNotify } from 'react-admin';
import { ProfessionalStatusEnum } from 'typings/enums';

export const AccountInfoStatus = 'account_info' as const;
export const ApprovedStatus = 'approved' as const;
export const ArchivedStatus = 'archived' as const;
export const PhoneScreenPendingStatus = 'phone_screen_pending' as const;
export const PhoneScreenScheduledStatus = 'phone_screen_scheduled' as const;
export const RejectedStatus = 'rejected' as const;
export const ResumeReviewStatus = 'resume_review' as const;

export type ApplicationStatus =
  | typeof AccountInfoStatus
  | typeof ApprovedStatus
  | typeof ArchivedStatus
  | typeof PhoneScreenPendingStatus
  | typeof PhoneScreenScheduledStatus
  | typeof RejectedStatus
  | typeof ResumeReviewStatus;

type ProfessionalScreeningData = {
  id?: number;
  professional_id?: number;
  reason?: string;
  scheduled_time?: string;
  screener_account_id?: number;
  screening_type?: 'phone' | 'written';
  canceled_at?: string;
};

type ProfessionalUpdateData = {
  application_status: ApplicationStatus;
  status?: ProfessionalStatusEnum;
  professional_screenings_attributes?: ProfessionalScreeningData;
  archive_reason?: string;
  reject_reason?: string;
};

// New flow (rescreening automation enabled)
const applicationStatusSelectionNew = {
  [ArchivedStatus]: [ResumeReviewItem, PhoneScreenPendingItem, RejectItem, AccountInfoItem],
  [ResumeReviewStatus]: [PhoneScreenPendingItem, ApproveItem, RejectItem, ArchiveItem],
  [PhoneScreenScheduledStatus]: [PhoneScreenPendingItem, ApproveItem, RejectItem, ArchiveItem],
  [PhoneScreenPendingStatus]: [PhoneScreenScheduledItem, ApproveItem, RejectItem, ArchiveItem],
  [RejectedStatus]: [PhoneScreenPendingItem],
};

// Legacy flow (rescreening automation disabled)
const applicationStatusSelectionLegacy = {
  [ArchivedStatus]: [
    ResumeReviewItem,
    PhoneScreenPendingItemLegacy,
    RejectItemLegacy,
    AccountInfoItem,
  ],
  [ResumeReviewStatus]: [
    PhoneScreenPendingItemLegacy,
    ApproveItemLegacy,
    RejectItemLegacy,
    ArchiveItemLegacy,
  ],
  [PhoneScreenScheduledStatus]: [
    PhoneScreenPendingItemLegacy,
    ApproveItemLegacy,
    RejectItemLegacy,
    ArchiveItemLegacy,
  ],
  [PhoneScreenPendingStatus]: [
    PhoneScreenScheduledItemLegacy,
    ApproveItemLegacy,
    RejectItemLegacy,
    ArchiveItemLegacy,
  ],
  [RejectedStatus]: [PhoneScreenPendingItemLegacy],
};

const getApplicationStatusSelection = (
  currentApplicationStatus: ApplicationStatus,
  isRescreeningEnabled: boolean,
) => {
  const selectionMap = isRescreeningEnabled
    ? applicationStatusSelectionNew
    : applicationStatusSelectionLegacy;
  const view = selectionMap[currentApplicationStatus];
  if (currentApplicationStatus === ApprovedStatus) {
    return [];
  }
  return view ?? [isRescreeningEnabled ? RejectItem : RejectItemLegacy];
};

const ApplicationStatusControl = () => {
  const { refetch } = useShowContext();
  const record = useRecordContext();
  const [update] = useUpdate();
  const notify = useNotify();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  if (!record?.application_status) {
    return null;
  }
  const currentApplicationStatus = record.application_status;
  const isRescreeningEnabled = record?.enable_rescreening_automation || false;

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const updateApplicationStatus = (status) => {
    const applicationStatus = status?.application_status;
    const willBeAproved = applicationStatus === ApprovedStatus;
    const isArchiveOrReject = [ArchivedStatus, RejectedStatus].includes(currentApplicationStatus);

    if (!isRescreeningEnabled || isArchiveOrReject) {
      update(
        'Professional',
        { id: record.id, data: status },
        {
          onSuccess: () => {
            refetch?.();
            handleClose();
          },
          onError: (error: any) => {
            notify(error.message || 'An error occurred', { type: 'error' });
          },
        },
      );
    } else {
      const {
        application_status: _omit,
        reject_reason: rejectReason,
        archive_reason: archiveReason,
        ...statusWithoutApplicationStatus
      } = status || {};
      const updateData: ProfessionalUpdateData = { application_status: applicationStatus };

      if (rejectReason) {
        updateData.reject_reason = rejectReason;
      }

      if (archiveReason) {
        updateData.archive_reason = archiveReason;
      }

      if (status?.cancel_existing_screening_id) {
        updateData.professional_screenings_attributes = {
          id: status.cancel_existing_screening_id,
          canceled_at: status.canceled_at
        };
      } else {
        const hasProfessionalScreeningData = Object.keys(statusWithoutApplicationStatus).length > 0;
        if (willBeAproved) {
          updateData.status = ProfessionalStatusEnum.active;
        }
        if (hasProfessionalScreeningData) {
          updateData.professional_screenings_attributes = statusWithoutApplicationStatus;
        }
      }

      update(
        'Professional',
        { id: record.id, data: updateData },
        {
          onSuccess: () => {
            refetch?.();
            handleClose();
          },
          onError: (error: any) => {
            notify(error.message || 'An error occurred', { type: 'error' });
          },
        },
      );
    }
  };

  const selections = getApplicationStatusSelection(currentApplicationStatus, isRescreeningEnabled);
  if (selections.length === 0) {
    return null;
  }

  return (
    <div>
      <Button size="small" variant="contained" onClick={handleClick} data-testid="actions-button">
        Actions ▼
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {selections.map((Item) => (
          <Item
            key={Item.name}
            currentApplicationStatus={currentApplicationStatus}
            updateApplicationStatus={updateApplicationStatus}
          />
        ))}
      </Menu>
    </div>
  );
};

export default ApplicationStatusControl;
